<script lang="ts">
	import ProductCard from './product/ProductCard.svelte'
	import { Splide, SplideSlide } from '@splidejs/svelte-splide'
	import { page } from '$app/state'
	import '@splidejs/splide/dist/css/themes/splide-default.min.css'
	import '$lib/css/components/splide.scss'
	import type { BaseProduct, LinkedProduct } from '$lib/js/elasticsearch/types'
	import { t } from 'svelte-i18n-lingui'
	import { getContextScope } from '$lib/js/helpers/i18n'
	import '$lib/css/components/rich-text.scss'

	interface Props {
		title?: string;
		description?: string;
		products?: BaseProduct[] | LinkedProduct[];
		type?: 'product' | 'eco' | 'promo' | 'default' | 'search' | 'tags' | '';
		promo?: 'fissler' | 'alessi' | '';
	}

	let {
		title = '',
		description = '',
		products = [],
		type = 'default',
		promo = ''
	}: Props = $props();

	const scope = getContextScope()

	let options = $state({
		type: 'loop',
		pagination: false,
		padding: '10px',
		perPage: 1,
		perMove: 1,
		autoplay: true,
		interval: 2500,
		mediaQuery: 'min',
		breakpoints: {},
		easing: 'cubic-bezier(0.37, 0, 0.63, 1)',
	})

	if (type === 'product') {
		options.breakpoints = {
			450: {
				perPage: 2,
			},
			1024: {
				perPage: 3,
				interval: 1600,
			},
			1300: {
				perPage: 4,
			},
		}
	} else if (type === 'search') {
		options.breakpoints = {
			450: {
				perPage: 2,
			},
			580: {
				perPage: 3,
			},
			900: {
				perPage: 4,
				interval: 1600,
			},
			1400: {
				perPage: 6,
			},
		}
	} else if (type === 'tags') {
		options.padding = '10px 0',
		options.breakpoints = {
			450: {
				perPage: 2,
			},
			900: {
				perPage: 3,
				interval: 1600,
			},
			1025: {
				perPage: 2,
			},
			1200: {
				perPage: 3,
			},
		}
	} else if (type === 'eco') {
		options.breakpoints = {
			450: {
				perPage: 2,
			},
		}
	} else if (type !== 'eco') {
		options.breakpoints = {
			450: {
				perPage: 2,
			},
			768: {
				perPage: 3,
				interval: 1600,
			},
			1024: {
				perPage: 5,
			},
		}
	}
</script>

<div class="products-slider u-slider {type} {promo}">
	{#if title}
		<div class="title-wrapper">
			<h2 class="title head-4">{title}</h2>

			{#if type === 'promo'}
				<a class="u-btn u-btn--light" href="{page.url.pathname}?p=1&promo=true">{$t`See all`}</a>
			{:else if promo === 'alessi'}
				<a class="promo-btn" href="/{scope}/brands/alessi">
					<img src="/images/promos/logo-alessi.jpg" alt="" title="" />
					<span class="icon icon-arr-right"></span>
				</a>
			{:else if promo === 'fissler'}
				<a class="promo-btn" href="/{scope}/brands/fissler">
					<img src="/images/promos/logo-fissler.png" alt="" title="" />
					<span class="icon icon-arr-right"></span>
				</a>
			{/if}

			{#if description}
				<div class="description u-rich-text">{@html description}</div>
			{/if}
		</div>
	{/if}

	{#if products.length === 1}
		<div class="card-single">
			<ProductCard lazy={true} product={products[0]} />
		</div>
	{:else if (products.length === 2 || products.length === 3) && type !== 'eco'}
		<div class="card-group__group">
			{#each products as product}
				<div class="card-group">
					<ProductCard lazy={true} {product} type="slider" />
				</div>
			{/each}
		</div>
	{:else}
		<div class="products-slider__inner">
			<Splide {options} aria-label={title || 'Products slider'}>
				{#each products as product}
					<SplideSlide>
						<ProductCard {product} type="slider" lazy={true} />
					</SplideSlide>
				{/each}
			</Splide>
		</div>
	{/if}
</div>

<style lang="scss">
	.promo {
		.title-wrapper {
			display: flex;
			flex-wrap: wrap;
			align-items: center;

			h2.title {
				margin: 10px 30px 10px 0;
				color: var(--main);
			}
		}
	}

	.alessi,
	.fissler {
		.title-wrapper {
			display: flex;
			flex-wrap: wrap;
			justify-content: space-between;
			align-items: center;
			margin-bottom: 20px;

			a {
				&:hover {
					span {
						transform: translateX(10px);
					}
				}

				img {
					width: 100px;
				}

				span {
					transition: all linear 0.125s;
					margin-left: 6px;
				}
			}
		}

		.promo-btn {
			display: flex;
			align-items: center;

			img {
				width: 100px;
				margin-right: 3px;
			}
		}
	}

	.cls-controller {
		height: 300px;
		width: 100%;
	}

	.card-single {
		width: 100%;

		@media (max-width: 449px) {
			margin-top: 12px;
		}

		@media (min-width: 769px) {
			max-width: 260px;
		}
	}

	.card-group {
		width: 100%;

		@media (min-width: 450px) {
			max-width: 50%;
		}

		@media (min-width: 1024px) {
			max-width: calc(100% / 3);
		}

		@media (min-width: 1300px) {
			max-width: 260px;
		}

		&__group {
			@media (min-width: 450px) {
				display: flex;
				flex-wrap: wrap;
				margin-left: -5px;
				margin-right: -5px;
			}
		}
	}

	.products-slider {
		text-align: left;

		.title {
			margin: 0;
			color: black;

			@media (min-width: 450px) {
				margin: 0 0 15px;
			}
		}

		.description {
			max-width: 650px;
			margin: 0 0 25px;
		}

		&__inner {
			position: relative;
			margin-left: -15px;
			margin-right: -15px;

			&::before,
			&::after {
				content: '';
				position: absolute;
				top: 0;
				bottom: 0;
				width: 30px;
				z-index: 1;
				pointer-events: none;
			}

			&::before {
				left: 0;
				background: linear-gradient(
					to right,
					rgba(255, 255, 255, 1) 0%,
					rgba(255, 255, 255, 0) 100%
				);
			}

			&::after {
				right: 0;
				background: linear-gradient(
					to left,
					rgba(255, 255, 255, 1) 0%,
					rgba(255, 255, 255, 0) 100%
				);
			}
		}

		&.tags {
			.products-slider {
				&__inner {
					&::before,
					&::after {
						display: none;
					}
				}
			}
		}

		&.eco {
			.products-slider {
				&__inner {
					@media (max-width: 449px) {
						margin-left: 0;
						margin-right: 0;
					}

					&::before {
						background: linear-gradient(
							to right,
							rgba(214, 244, 236, 1) 0%,
							rgba(214, 244, 236, 0) 100%
						);
					}

					&::after {
						background: linear-gradient(
							to left,
							rgba(214, 244, 236, 1) 0%,
							rgba(214, 244, 236, 0) 100%
						);
					}
				}
			}
		}
	}
</style>
